import * as React from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import {
  FinancialWorkspaceAvailabilitiesDocument,
  GetLegalEntityFinanceTaxTypesDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const RootPage = React.lazy(() => import('modules/finance/pages/settings/RootPage'))

const BankAccountPage = React.lazy(() => import('modules/finance/pages/Accounting/BankAccountPage'))
const CustomCategoriesPage = React.lazy(
  () => import('modules/finance/pages/Accounting/CustomCategoriesPage')
)
const VendorPage = React.lazy(() => import('modules/finance/pages/Accounting/VendorPage'))

const CostCentersPage = React.lazy(() => import('modules/finance/pages/CostCentersPage'))
const CostCentersListPage = React.lazy(() => import('modules/finance/pages/CostCentersListPage'))

const CostCentersListPageEdit = React.lazy(
  () => import('modules/finance/pages/CostCentersListPage/Edit')
)
const CostCentersListPageDelete = React.lazy(
  () => import('modules/finance/pages/CostCentersListPage/Delete')
)
const CostCentersListPageImport = React.lazy(
  () => import('modules/finance/pages/CostCentersListPage/Import')
)
const CostCentersPeoplePage = React.lazy(
  () => import('modules/finance/pages/CostCentersPeoplePage')
)
const CostCentersPeopleDetailsPage = React.lazy(
  () => import('modules/finance/pages/CostCentersPeopleDetailsPage')
)
const CostCentersPeopleCreatePage = React.lazy(
  () => import('modules/finance/pages/CostCenterPeopleCreatePage')
)

const CreateCostCenterModalPage = React.lazy(
  () => import('modules/finance/pages/CostCentersListPage/Create')
)

const SalesSettingsPage = React.lazy(() => import('modules/finance/pages/settings/Sales/index'))

const AccountingSettingsPage = React.lazy(
  () => import('modules/finance/pages/settings/Acccounting')
)

const DefaultConfigurationPage = React.lazy(
  () => import('modules/finance/pages/Accounting/DefaultConfigurationPage')
)
const TaxTypeDetailPage = React.lazy(
  () => import('modules/finance/pages/Accounting/TaxTypeDetailPage')
)
const TaxTypePage = React.lazy(() => import('modules/finance/pages/Accounting/TaxTypePage'))
const PayrollConceptPage = React.lazy(
  () => import('modules/finance/pages/Accounting/PayrollConceptPage')
)

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.finance.path,
      element: <RootPage />,
      children: [
        {
          index: true,
          element: <Navigate to={navigation.settings.finance.costCenters.path} replace />,
        },
        {
          path: navigation.settings.finance.sales.path,
          element: <Outlet />,
          handle: {
            meta: () => ({
              title: i18n.t('finance.settings.sales.title'),
            }),
          },
          children: [
            {
              index: true,
              element: <SalesSettingsPage />,
            },
          ],
        },
        {
          path: navigation.settings.finance.accounting.path,
          handle: {
            meta: () => ({
              title: i18n.t('finance.settings.accounting.title'),
            }),
          },
          children: [
            {
              index: true,
              element: <AccountingSettingsPage />,
            },
            {
              path: navigation.settings.finance.accounting.expenseCategory.path,
              element: <CustomCategoriesPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.custom_categories.label'),
                  illustration: 'checks',
                  description: i18n.t('finance.settings.accounting.custom_categories.description'),
                }),
              },
            },
            {
              path: navigation.settings.finance.accounting.bankAccount.path,
              element: <BankAccountPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.bank_account.label'),
                  description: i18n.t('finance.settings.accounting.bank_account.description'),
                }),
              },
            },
            {
              path: navigation.settings.finance.accounting.vendor.path,
              element: <VendorPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.vendor.label'),
                  description: i18n.t('finance.settings.accounting.vendor.description'),
                }),
              },
            },
            {
              path: navigation.settings.finance.accounting.taxType.path,
              element: <TaxTypePage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.tax_type.label'),
                  description: i18n.t('finance.settings.accounting.tax_type.description'),
                  illustration: 'coins',
                }),
              },
              children: [
                {
                  path: navigation.settings.finance.accounting.taxType.taxTypeId.path,
                  element: <TaxTypeDetailPage />,
                  handle: {
                    meta: ({ params, watchQuery }) =>
                      watchQuery({
                        query: GetLegalEntityFinanceTaxTypesDocument,
                        variables: {
                          ids: [Number(params['id'])],
                        },
                      }).map(({ data }) => {
                        const taxType = data?.finance?.taxTypes?.at(0)
                        const taxTypeName = taxType ? taxType.name : ''
                        return {
                          title: taxTypeName,
                          illustration: 'documents',
                          description: i18n.t('finance.settings.taxes.tax_type.description'),
                        }
                      }),
                  },
                },
              ],
            },
            {
              path: navigation.settings.finance.accounting.defaultConfiguration.path,
              element: <DefaultConfigurationPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.default_configuration.label'),
                  description: i18n.t(
                    'finance.settings.accounting.default_configuration.description'
                  ),
                  illustration: 'settings',
                }),
              },
            },
            {
              path: navigation.settings.finance.accounting.payrollConcept.path,
              element: <PayrollConceptPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.payroll_concept.label'),
                  description: i18n.t('finance.settings.accounting.payroll_concept.description'),
                }),
              },
            },
          ],
        },
        {
          path: navigation.settings.finance.costCenters.path,
          element: <CostCentersPage />,
          handle: {
            resource: true,
            meta: ({ watchQuery, session }) => {
              return watchQuery({
                query: FinancialWorkspaceAvailabilitiesDocument,
                variables: {
                  companyId: session.company.id,
                },
              }).map(({ data }) => {
                const available =
                  data?.features?.featuresConnection?.nodes.find(
                    (feature) => feature.name === 'dev_fw_test_testdeverdad'
                  )?.enabled ?? false

                return {
                  title: i18n.t('finance.settings.cost_centers.title'),
                  subNavigation: [
                    {
                      id: 'list',
                      isVisible: true,
                      title: i18n.t('finance.settings.cost_centers.structure'),
                      href: navigation.settings.finance.costCenters.list.fullPath(),
                    },
                    {
                      id: 'people',
                      isVisible: true,
                      title: i18n.t('finance.settings.cost_centers.assignments'),
                      href: navigation.settings.finance.costCenters.people.fullPath(),
                    },
                  ],
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <ReroutingIndex />,
            },
            {
              path: navigation.settings.finance.costCenters.list.path,
              element: <CostCentersListPage />,
              children: [
                {
                  path: navigation.settings.finance.costCenters.list.newCostCenter.path,
                  element: <CreateCostCenterModalPage />,
                },
                {
                  path: navigation.settings.finance.costCenters.delete.path,
                  element: <CostCentersListPageDelete />,
                },
                {
                  path: navigation.settings.finance.costCenters.list.edit.path,
                  element: <CostCentersListPageEdit />,
                },
                {
                  path: navigation.settings.finance.costCenters.list.import.path,
                  element: (
                    <Routes>
                      <Route path="" element={<CostCentersListPageImport />} />
                    </Routes>
                  ),
                },
              ],
            },
            {
              path: navigation.settings.finance.costCenters.people.path,
              element: <CostCentersPeoplePage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.cost_centers.title'),
                }),
              },
              children: [
                {
                  path: navigation.settings.finance.costCenters.people.create.path,
                  element: (
                    <Routes>
                      <Route path="" element={<CostCentersPeopleCreatePage />} />
                    </Routes>
                  ),
                },
                {
                  path: navigation.settings.finance.costCenters.people.edit.path,
                  element: <CostCentersPeopleDetailsPage />,
                },
              ],
            },
            {
              path: navigation.settings.finance.costCenters.people.path,
              element: <CostCentersPeoplePage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.cost_centers.title'),
                }),
              },
              children: [
                {
                  path: navigation.settings.finance.costCenters.people.create.path,
                  element: (
                    <Routes>
                      <Route path="" element={<CostCentersPeopleCreatePage />} />
                    </Routes>
                  ),
                },
                {
                  path: navigation.settings.finance.costCenters.people.edit.path,
                  element: <CostCentersPeopleDetailsPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
